import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useState, useEffect, useMemo, useRef, useCallback, useContext } from 'react';
import Button from '@/components/school-management/Button/Button';
import { SchoolDetails, Class as BaseClass } from '@/admin/config/listConfig';
import styles from '@/components/school-management/SchoolDetailsPage/styles.module.scss';
import { FieldConfig, FormConfig } from '@/school-management/config/formConfig';
import { useSchoolDetails } from '@/school-management/hooks/useSchoolDetails';
import usePageVisibility from '@/hooks/UsePageVisibility';
import { ROUTES } from '@/admin/config/routes';

import GeneralDetails from './GeneralDetails';
import Teachers from './Teachers';
import Classes from './Classes';
import Students from '@/components/school-management/SchoolDetailsPage/Students';
import SchoolStatistics, {
  SchoolStatisticsRef,
} from '@/components/school-management/SchoolDetailsPage/SchoolStatistics';
import {
  FilterField,
  FilterValue,
  isEmptyValue,
} from '@/components/school-management/BimloaFilter/BimloaFilter';
import type { CascadingFilterRequest } from '@/school-management/types/response/autocomplete';
import type {
  TeacherAutocompleteItem,
  StudentAutocompleteItem,
  GroupAutocompleteItem,
  ClassCodeAutocompleteItem,
} from '@/school-management/types/response/autocomplete';
import commonUtils from '@/school-management/utils/common';
import { hasStudentTabStatsScopeFilters } from '@/school-management/utils/userStatisticsFilters';
import { TeacherReportItem } from '@/school-management/types/response';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import PageHeader from '@/components/school-management/GenericTable/PageHeader/PageHeader';
import ActionNotification from '@/components/school-management/ActionNotification/ActionNotification';
import DealLimitReachedMessage from '@/components/DealLimitReachedMessage/DealLimitReachedMessage';
import { UserRole } from '@/school-management/types/roles';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import { icons } from '@/school-management/utils/icons';
import Popup from '@/components/school-management/Popup/Popup';
import Input from '@/components/school-management/Input/Input';
import { adminManagementService } from '@/admin/services/adminManagement';
import { registerService } from '@/admin/services/register';
import { teacherService } from '@/school-management/services/teacher';
import PageLoader from '@/components/school-management/PageLoader/PageLoader';
import stylesZero from './zeroState.module.scss';
import PageTabs from '@/components/school-management/PageTabs/PageTabs';
import StudentMetricsDashboard from '@/components/school-management/StudentMetricsDashboard/StudentMetricsDashboard';
import useStudentMetricsDashboard from '@/components/school-management/StudentMetricsDashboard/useStudentMetricsDashboard';
import { useStudents } from '@/admin/hooks/useStudents';
import config from '@/config';
import { EventDispatcherContext, DispatcherEvents } from '@/context/eventDispatcherContext';

// ELL-6657: To allow keeping metrics view on refresh we are adding these params to the URL
const STUDENT_METRICS_VIEW_PARAM = 'studentsView';
const STUDENT_METRICS_DRILL_DOWN_PARAM = 'studentsDrillDown';
const STUDENT_METRICS_DRILL_DOWN_VALUES = [
  'AvgStudentGrowth',
  'AvgStudentProgressAtCurrentLevel',
  'StudentLevelProgression',
  'AvgScores',
  'StudentsAcrossSkills',
] as const;

const METRICS_FILTERS_STORAGE_KEY_PREFIX = 'schoolDetails_metrics_filters_';
const TEACHERS_FILTERS_STORAGE_KEY_PREFIX = 'schoolDetails_teachers_filters_';
const CLASSES_FILTERS_STORAGE_KEY_PREFIX = 'schoolDetails_classes_filters_';
const STUDENTS_DATA_FILTERS_STORAGE_KEY_PREFIX = 'schoolDetails_students_data_filters_';
const REFRESH_ON_RETURN_COOLDOWN_MS = 30_000;

const DEFAULT_TEACHER_FILTERS: Record<string, FilterValue> = {
  teachers: [],
  firstLogin: { mode: 'none' },
  lastLogin: { mode: 'none' },
};
const DEFAULT_CLASS_FILTERS: Record<string, FilterValue> = {
  groupName: [],
  teacher: [],
  grade: [],
  classCode: [],
};
const DEFAULT_STUDENT_FILTERS: Record<string, FilterValue> = {
  name: [],
  teacher: [],
  groupName: [],
  gradeName: [],
  startingLevel: [],
  currentLevel: [],
  status: [],
  completedActivities: {},
  score: {},
  firstLogin: { mode: 'none' },
  lastLogin: { mode: 'none' },
  displayDataFor: 'currentLevel',
};

// Helper data and types that are used by child components, so they are kept here
// Or they could be moved to a separate types/data file.

type Student = {
  id: string;
  name: string;
  email: string;
  status: string;
  progress: number;
  score: number | string;
};
type ClassWithStudents = BaseClass & { studentList: Student[] };

// Add class form config
const createClassFormConfig = (
  teachers: TeacherReportItem[],
  t: (key: string) => string
): FormConfig => ({
  onSubmit: () => {},
  fields: [
    {
      type: 'title' as const,
      name: 'new-class-title',
      MainTitle: t('schoolDetailsPage.classes.newClass'),
      gridSpan: 12,
    },
    {
      type: 'text' as const,
      name: 'className',
      label: t('schoolDetailsPage.classes.className'),
      placeholder: t('schoolDetailsPage.classes.placeholderWriteHere'),
      gridSpan: 4,
      maxLength: 40,
      enforceMaxLength: false,
      validation: {
        required: true,
        pattern: /^[A-Za-z0-9\s'"()–\-:/&*.,֐-׿À-ÿĀ-ǿȀ-ʯ]+$/,
      },
    },
    {
      type: 'select' as const,
      name: 'grade',
      label: t('schoolDetailsPage.classes.gradeLabel'),
      placeholder: t('schoolDetailsPage.classes.teachersPlaceholder'),
      options: [
        { label: t('schoolDetailsPage.classes.gradeOption1st'), value: '1st' },
        { label: t('schoolDetailsPage.classes.gradeOption2nd'), value: '2nd' },
        { label: t('schoolDetailsPage.classes.gradeOption3rd'), value: '3rd' },
      ],
      gridSpan: 4,
      validation: { required: true },
    },
    {
      type: 'multi-select' as const,
      name: 'teachers',
      label: t('schoolDetailsPage.classes.teachersLabel'),
      placeholder: t('schoolDetailsPage.classes.teachersPlaceholder'),
      searchPlaceholder: t('schoolDetailsPage.classes.teachersSearchPlaceholder'),
      options: teachers.map((t: TeacherReportItem) => ({
        label: t.firstName + ' ' + t.lastName,
        value: t.userId,
        data: { email: t.email },
      })),
      gridSpan: 4,
      maxSelections: 2,
      validation: {
        required: true,
      },
    },
  ] as FieldConfig[],
});

const hasAnyFilter = (filterFields: FilterField[], filters: Record<string, FilterValue>) => {
  const anyActive = filterFields.some(f => {
    return f && filters[f.key] ? !isEmptyValue(f, filters[f.key]) : false;
  });
  return anyActive;
};

const SchoolDetailsPage = () => {
  const { schoolId } = useParams<{ schoolId: string }>();
  const location = useLocation();
  const { userRole, user, token, loggedIn, appLoaded } = useAppSelector(authSelector);
  const resolvedSchoolId = user?.metadata?.organizationDetails?.organizationId;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useAdminTranslation();
  // Redirect to login if not authenticated (only after app has loaded to avoid premature redirects)
  useEffect(() => {
    if (!appLoaded) return;

    const currentToken = localStorage.getItem('token');
    if (!currentToken || !token || !loggedIn || userRole === 'guest') {
      navigate(ROUTES.LOGIN, { replace: true });
    }
  }, [token, loggedIn, userRole, appLoaded, navigate]);

  // API data hook
  const {
    data: apiData,
    isLoading,
    error,
    refetch,
    refetchInBackground,
    retry,
    canRetry,
    isRetrying,
    clearError,
  } = useSchoolDetails(resolvedSchoolId || undefined);

  const organizationId = user?.metadata?.organizationDetails?.organizationId;

  const activeTab = searchParams.get('tab') || 'general';
  const [editTeacherModalOpen, setEditTeacherModalOpen] = useState(false);
  const [editTeacherData, setEditTeacherData] = useState<any>(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [teachers, setTeachers] = useState<TeacherReportItem[]>([]);
  const [addingClass, setAddingClass] = useState(false);
  const [addingTeacher, setAddingTeacher] = useState(false);
  const [editClass, setEditClass] = useState(false);
  const [showStudentInvite, setShowStudentInvite] = useState(false);
  const [newClassDraft, setNewClassDraft] = useState<Partial<ClassWithStudents> | null>(null);
  const [classes, setClasses] = useState<ClassWithStudents[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [hasStudentsFromComponent, setHasStudentsFromComponent] = useState<boolean | null>(null);
  const [teacherClassesMode, setTeacherClassesMode] = useState<boolean>(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifMessage, setNotifMessage] = useState('');
  const [notifType, setNotifType] = useState<'success' | 'error' | 'info'>('success');
  const [showDealLimitReachedModal, setShowDealLimitReachedModal] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [newSchoolName, setNewSchoolName] = useState('');
  const [renameError, setRenameError] = useState('');
  const [currentSchoolName, setCurrentSchoolName] = useState<string>('');
  const [showClassesStatistics, setShowClassesStatistics] = useState<boolean>(false);
  const [showTeachersStatistics, setShowTeachersStatistics] = useState<boolean>(false);
  const [showStudentsStatistics, setShowStudentsStatistics] = useState<boolean>(false);
  const [showTeachersStatsSection, setShowTeachersStatsSection] = useState<boolean>(true);
  const [showClassesStatsSection, setShowClassesStatsSection] = useState<boolean>(true);
  const [showStudentsStatsSection, setShowStudentsStatsSection] = useState<boolean>(true);
  const [hasSelectedStudents, setHasSelectedStudents] = useState<boolean>(false);
  const [hasSelectedClasses, setHasSelectedClasses] = useState<boolean>(false);
  const schoolStatisticsRef = useRef<SchoolStatisticsRef>(null);
  const eventDispatcherContext = useContext(EventDispatcherContext);

  const studentsTabView =
    activeTab === 'students' && searchParams.get(STUDENT_METRICS_VIEW_PARAM) === 'metrics'
      ? 'metrics'
      : 'data';

  const refreshSchoolDetails = useCallback(() => {
    eventDispatcherContext.dispatch(DispatcherEvents.SCHOOL_DETAILS_CHANGE, null);
  }, [eventDispatcherContext]);

  useEffect(() => {
    eventDispatcherContext.addListener(DispatcherEvents.SCHOOL_DETAILS_CHANGE, refetchInBackground);

    return () => {
      eventDispatcherContext.removeListener(
        DispatcherEvents.SCHOOL_DETAILS_CHANGE,
        refetchInBackground
      );
    };
  }, [refetchInBackground, eventDispatcherContext]);

  const { isVisible: isPageVisible } = usePageVisibility();
  const wasPageVisibleRef = useRef(isPageVisible);
  const isFirstGeneralTabRef = useRef(true);

  const lastVisibilityRefreshRef = useRef(0);

  useEffect(() => {
    isFirstGeneralTabRef.current = true;
  }, [resolvedSchoolId]);

  useEffect(() => {
    if (!resolvedSchoolId) return;
    if (isPageVisible && !wasPageVisibleRef.current) {
      const now = Date.now();
      if (now - lastVisibilityRefreshRef.current >= REFRESH_ON_RETURN_COOLDOWN_MS) {
        lastVisibilityRefreshRef.current = now;
        refreshSchoolDetails();
      }
    }
    wasPageVisibleRef.current = isPageVisible;
  }, [isPageVisible, resolvedSchoolId, refreshSchoolDetails]);

  useEffect(() => {
    if (activeTab !== 'general' || !resolvedSchoolId) return;
    if (isFirstGeneralTabRef.current) {
      isFirstGeneralTabRef.current = false;
      return;
    }
    refreshSchoolDetails();
  }, [activeTab, resolvedSchoolId, refreshSchoolDetails]);

  useEffect(() => {
    if (activeTab === 'teachers') setShowTeachersStatsSection(true);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'classes') setShowClassesStatsSection(true);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'classes') {
      setHasSelectedClasses(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'students' && studentsTabView === 'data') {
      setShowStudentsStatsSection(true);
    }
  }, [activeTab, studentsTabView]);

  const setStudentsTabView = useCallback(
    (view: string) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (view === 'metrics') next.set(STUDENT_METRICS_VIEW_PARAM, 'metrics');
        else next.delete(STUDENT_METRICS_VIEW_PARAM);
        return next;
      });
    },
    [setSearchParams]
  );

  const metricsFiltersRestoredRef = useRef(false);
  const teachersFiltersRestoredRef = useRef(false);
  const classesFiltersRestoredRef = useRef(false);
  const studentsDataFiltersRestoredRef = useRef(false);

  // Update school name when API data loads (only on initial load)
  useEffect(() => {
    const apiSchoolName = apiData?.data?.data?.school?.name;
    if (apiSchoolName && !currentSchoolName) {
      setCurrentSchoolName(apiSchoolName);
    }
  }, [apiData]);

  const handleCreateNewTeacher = async () => {
    setAddingTeacher(true);
  };
  const handleCreateNewClass = useCallback(async () => {
    let isAllowedToRegister = false;
    try {
      isAllowedToRegister = !(await registerService.isRegistrationLimitReached(user))?.data.data;
    } catch (error) {
      isAllowedToRegister = false;
    }

    setAddingClass(true);
    setEditClass(false);
    navigate(ROUTES.CREATE_CLASS(resolvedSchoolId || schoolId || ''), {
      state: {
        allowAddingStudents: isAllowedToRegister,
      },
    });
  }, [user, navigate, resolvedSchoolId, schoolId]);

  const handleCloseDealLimitReachedModal = useCallback(() => {
    setShowDealLimitReachedModal(false);
  }, []);

  const {
    students: baseStudents,
    isLoading: studentsLoading,
    error: studentsError,
    reload: reloadStudents,
  } = useStudents(resolvedSchoolId || undefined, {
    enabled: false,
  });

  const {
    drillDownType: studentMetricsDrillDownType,
    setDrillDownType: setStudentDashboardDrillDownType,
    filters: studentDashboardFilters,
    setFilters: setStudentDashboardFilters,
    filterFields: studentDashboardFiltersFields,
    dashboardFilter: studentDashboardApiFilter,
  } = useStudentMetricsDashboard();
  const studentDashboardToolsRef = useRef(null);

  // Persist metrics drill-down in URL so it survives refresh
  const setStudentMetricsDrillDown = useCallback(
    (type: '' | (typeof STUDENT_METRICS_DRILL_DOWN_VALUES)[number]) => {
      setStudentDashboardDrillDownType(type);
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (type) next.set(STUDENT_METRICS_DRILL_DOWN_PARAM, type);
        else next.delete(STUDENT_METRICS_DRILL_DOWN_PARAM);
        return next;
      });
    },
    [setStudentDashboardDrillDownType, setSearchParams]
  );

  const [studentFilters, setStudentFilters] = useState<Record<string, FilterValue>>(() => ({
    ...DEFAULT_STUDENT_FILTERS,
  }));

  useEffect(() => {
    if (activeTab !== 'students') {
      setHasStudentsFromComponent(null);
    }
  }, [activeTab]);

  useEffect(() => {
    // Sync metrics drill-down from URL so drill-down view persists on refresh
    if (activeTab === 'students') {
      const urlDrill = searchParams.get(STUDENT_METRICS_DRILL_DOWN_PARAM);
      const valid = urlDrill && STUDENT_METRICS_DRILL_DOWN_VALUES.includes(urlDrill as any);
      if (valid) setStudentDashboardDrillDownType(urlDrill as any);
      else setStudentDashboardDrillDownType('');
    }
  }, [activeTab, searchParams]);

  useEffect(() => {
    metricsFiltersRestoredRef.current = false;
    teachersFiltersRestoredRef.current = false;
    classesFiltersRestoredRef.current = false;
    studentsDataFiltersRestoredRef.current = false;
  }, [resolvedSchoolId, schoolId]);

  useEffect(() => {
    if (
      activeTab !== 'students' ||
      studentsTabView !== 'metrics' ||
      !(resolvedSchoolId || schoolId)
    ) {
      return;
    }
    const storageKey = `${METRICS_FILTERS_STORAGE_KEY_PREFIX}${resolvedSchoolId || schoolId}`;
    if (!metricsFiltersRestoredRef.current) {
      metricsFiltersRestoredRef.current = true;
      try {
        const stored = sessionStorage.getItem(storageKey);
        if (stored) {
          const parsed = JSON.parse(stored) as Record<string, FilterValue>;
          if (parsed && typeof parsed === 'object') {
            setStudentDashboardFilters(prev => ({ ...prev, ...parsed }));
          }
        }
      } catch {}
    }
  }, [activeTab, studentsTabView, resolvedSchoolId, schoolId, setStudentDashboardFilters]);

  // Persist metrics filters to sessionStorage when they change (while on Metrics tab)
  useEffect(() => {
    if (
      activeTab !== 'students' ||
      studentsTabView !== 'metrics' ||
      !(resolvedSchoolId || schoolId)
    ) {
      return;
    }
    const storageKey = `${METRICS_FILTERS_STORAGE_KEY_PREFIX}${resolvedSchoolId || schoolId}`;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(studentDashboardFilters));
    } catch {}
  }, [activeTab, studentsTabView, resolvedSchoolId, schoolId, studentDashboardFilters]);

  const schoolScopeId = resolvedSchoolId || schoolId;

  // Restore students (data) filters from sessionStorage when entering Students tab (data view)
  useEffect(() => {
    if (activeTab !== 'students' || studentsTabView !== 'data' || !schoolScopeId) {
      return;
    }
    if (!studentsDataFiltersRestoredRef.current) {
      studentsDataFiltersRestoredRef.current = true;
      try {
        const stored = sessionStorage.getItem(
          `${STUDENTS_DATA_FILTERS_STORAGE_KEY_PREFIX}${schoolScopeId}`
        );
        if (stored) {
          const parsed = JSON.parse(stored) as Record<string, FilterValue>;
          if (parsed && typeof parsed === 'object') {
            setStudentFilters(prev => ({ ...DEFAULT_STUDENT_FILTERS, ...prev, ...parsed }));
          }
        }
      } catch {}
    }
  }, [activeTab, studentsTabView, schoolScopeId]);

  // Persist students (data) filters when they change (while on Students tab, data view)
  useEffect(() => {
    if (activeTab !== 'students' || studentsTabView !== 'data' || !schoolScopeId) {
      return;
    }
    try {
      sessionStorage.setItem(
        `${STUDENTS_DATA_FILTERS_STORAGE_KEY_PREFIX}${schoolScopeId}`,
        JSON.stringify(studentFilters)
      );
    } catch {}
  }, [activeTab, studentsTabView, schoolScopeId, studentFilters]);

  // When on Classes tab, fetch teachers so we know whether to enable/disable the Add class button
  const teachersScopeId = resolvedSchoolId || schoolId;
  useEffect(() => {
    if (activeTab !== 'classes' || !teachersScopeId) return;
    let cancelled = false;
    teacherService
      .getAllTeachers(teachersScopeId, { page: 1, pageSize: 1 })
      .then(response => {
        if (!cancelled) {
          setTeachers(response?.data?.items ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) setTeachers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, teachersScopeId]);

  const studentFilterFields: FilterField[] = useMemo(() => {
    const allGrades = commonUtils.getAllGradesForDropdown(t);
    const gradeNames = [...allGrades.map(g => g.label)];

    // Static level options (1-5)
    const startingLevels = Array.from({ length: 5 }, (_, i) => String(i + 1));
    const currentLevels = Array.from({ length: 5 }, (_, i) => String(i + 1));

    const isClassCodeEnabled = !!apiData?.data?.data?.school?.classCodeEnabled;
    const userStatus = isClassCodeEnabled
      ? [t('active'), t('resetPassword'), t('levelTestIncomplete')]
      : [t('active'), t('inactive'), t('levelTestIncomplete')];

    const filter: FilterField[] = [
      {
        key: 'name',
        label: t('studentMetricsDashboard.filters.studentName') || 'Student Name',
        options: [],
        multi: true,
        autocompleteType: 'students',
        searchPlaceholder:
          t('studentMetricsDashboard.filters.studentNameSearchPlaceholder') || 'Search student',
      },
      {
        key: 'teacher',
        label: t('studentMetricsDashboard.filters.teacher') || 'Teacher Name',
        options: [],
        multi: true,
        autocompleteType: 'teachers',
        searchPlaceholder:
          t('studentMetricsDashboard.filters.teacherSearchPlaceholder') || 'Search teacher',
      },
      {
        key: 'groupName',
        label: t('studentMetricsDashboard.filters.group') || 'Class Name',
        options: [],
        multi: true,
        autocompleteType: 'groups',
        searchPlaceholder:
          t('studentMetricsDashboard.filters.groupSearchPlaceholder') || 'Search class',
      },
      {
        key: 'gradeName',
        label: t('studentMetricsDashboard.filters.grade') || 'Grade',
        options: gradeNames,
        multi: true,
        searchable: true,
        searchPlaceholder:
          t('studentMetricsDashboard.filters.gradeSearchPlaceholder') || 'Search grade',
      },
      {
        key: 'startingLevel',
        label: t('studentMetricsDashboard.filters.startingLevel') || 'Starting Level',
        options: startingLevels,
        multi: true,
        searchPlaceholder:
          t('studentMetricsDashboard.filters.startingLevelSearchPlaceholder') || 'Search level',
      },
      {
        key: 'currentLevel',
        label: t('studentMetricsDashboard.filters.currentLevel') || 'Current Level',
        options: currentLevels,
        multi: true,
        searchPlaceholder:
          t('studentMetricsDashboard.filters.currentLevelSearchPlaceholder') || 'Search level',
      },
      {
        key: 'status',
        label: t('studentMetricsDashboard.filters.status') || 'Status',
        options: userStatus,
        multi: true,
        searchPlaceholder:
          t('studentMetricsDashboard.filters.statusSearchPlaceholder') || 'Search status',
      },
      {
        key: 'completedActivities',
        label: t('studentMetricsDashboard.filters.completedActivities') || 'Completed Activities',
        options: [],
        numberRangeMode: {
          min: 0,
          // No max limit - users can enter any number
        },
      },
      {
        key: 'score',
        label: t('studentMetricsDashboard.filters.score') || 'Score',
        options: [],
        numberRangeMode: {
          min: 0,
          max: 100,
        },
      },
      // {
      //   key: 'timeSpent',
      //   label: t('studentMetricsDashboard.filters.timeSpent') || 'Time Spent (hours)',
      //   options: [],
      //   numberRangeMode: {
      //     min: 0,
      //     // No max limit - users can enter any number of hours
      //   },
      // },
      {
        key: 'firstLogin',
        label: t('studentMetricsDashboard.filters.firstLogin') || 'First Login',
        options: ['last7', 'last30', 'specific', 'range'] as string[],
        dateMode: true,
      },
      {
        key: 'lastLogin',
        label: t('studentMetricsDashboard.filters.lastLogin') || 'Last Login',
        options: ['last7', 'last30', 'specific', 'range'] as string[],
        dateMode: true,
      },
    ];

    return filter;
  }, [t, apiData]);

  // Note: Client-side filtering removed - Students component handles server-side filtering via API
  // The studentFilters are passed to the Students component which passes them to the API

  const gradeOptions = [
    { label: 'All', value: 'All' },
    ...Array.from(new Set(classes.map(c => c.grade)))
      .sort()
      .map(g => ({ label: g, value: g })),
  ];

  const [selectedGrade, setSelectedGrade] = useState(gradeOptions[0]);

  const [classFilters, setClassFilters] = useState<Record<string, FilterValue>>(() => ({
    ...DEFAULT_CLASS_FILTERS,
  }));

  const [teacherFilters, setTeacherFilters] = useState<Record<string, FilterValue>>(() => ({
    ...DEFAULT_TEACHER_FILTERS,
  }));

  // Restore teachers filters from sessionStorage when entering Teachers tab
  useEffect(() => {
    if (activeTab !== 'teachers' || !schoolScopeId) return;
    if (!teachersFiltersRestoredRef.current) {
      teachersFiltersRestoredRef.current = true;
      try {
        const stored = sessionStorage.getItem(
          `${TEACHERS_FILTERS_STORAGE_KEY_PREFIX}${schoolScopeId}`
        );
        if (stored) {
          const parsed = JSON.parse(stored) as Record<string, FilterValue>;
          if (parsed && typeof parsed === 'object') {
            setTeacherFilters(prev => ({ ...DEFAULT_TEACHER_FILTERS, ...prev, ...parsed }));
          }
        }
      } catch {}
    }
  }, [activeTab, schoolScopeId]);

  // Persist teachers filters when they change (while on Teachers tab)
  useEffect(() => {
    if (activeTab !== 'teachers' || !schoolScopeId) return;
    try {
      sessionStorage.setItem(
        `${TEACHERS_FILTERS_STORAGE_KEY_PREFIX}${schoolScopeId}`,
        JSON.stringify(teacherFilters)
      );
    } catch {}
  }, [activeTab, schoolScopeId, teacherFilters]);

  // Restore classes filters from sessionStorage when entering Classes tab
  useEffect(() => {
    if (activeTab !== 'classes' || !schoolScopeId) return;
    if (!classesFiltersRestoredRef.current) {
      classesFiltersRestoredRef.current = true;
      try {
        const stored = sessionStorage.getItem(
          `${CLASSES_FILTERS_STORAGE_KEY_PREFIX}${schoolScopeId}`
        );
        if (stored) {
          const parsed = JSON.parse(stored) as Record<string, FilterValue>;
          if (parsed && typeof parsed === 'object') {
            setClassFilters(prev => ({ ...DEFAULT_CLASS_FILTERS, ...prev, ...parsed }));
          }
        }
      } catch {}
    }
  }, [activeTab, schoolScopeId]);

  // Persist classes filters when they change (while on Classes tab)
  useEffect(() => {
    if (activeTab !== 'classes' || !schoolScopeId) return;
    try {
      sessionStorage.setItem(
        `${CLASSES_FILTERS_STORAGE_KEY_PREFIX}${schoolScopeId}`,
        JSON.stringify(classFilters)
      );
    } catch {}
  }, [activeTab, schoolScopeId, classFilters]);

  const teacherFilterFields: FilterField[] = useMemo(
    () => [
      {
        key: 'teachers',
        label: t('schoolDetailsPage.teachers.filters.teachers'),
        options: [],
        multi: true,
        autocompleteType: 'teachers',
        searchPlaceholder: t('schoolDetailsPage.teachers.filters.teachersSearchPlaceholder') || '',
      },
      {
        key: 'firstLogin',
        label: t('genericTable.teachers.firstLogin'),
        options: ['last7', 'last30', 'specific', 'range'],
        dateMode: true,
      },
      {
        key: 'lastLogin',
        label: t('genericTable.teachers.lastLogin'),
        options: ['last7', 'last30', 'specific', 'range'],
        dateMode: true,
      },
    ],
    [t]
  );

  const classFilterFields: FilterField[] = useMemo(() => {
    const allGrades = commonUtils.getAllGradesForDropdown(t);
    const gradeNames = [...allGrades.map(g => g.label)];
    const isClassCodeEnabled = !!apiData?.data?.data?.school?.classCodeEnabled;

    return [
      {
        key: 'groupName',
        label: t('genericTable.classes.name'),
        options: [],
        multi: true,
        autocompleteType: 'groups',
        searchPlaceholder:
          t('schoolDetailsPage.classes.filters.classSearchPlaceholder') || 'Search class',
      },
      ...(isClassCodeEnabled
        ? [
            {
              key: 'classCode',
              label: t('schoolDetailsPage.classes.filters.classCode'),
              options: [],
              multi: true,
              autocompleteType: 'class_code',
              searchPlaceholder:
                t('schoolDetailsPage.classes.filters.classCodePlaceholder') || 'Search class code',
            },
          ]
        : []),
      {
        key: 'teacher',
        label: t('genericTable.classes.teacher'),
        options: [],
        multi: true,
        autocompleteType: 'teachers',
        searchPlaceholder:
          t('schoolDetailsPage.classes.filters.teacherSearchPlaceholder') || 'Search teacher',
      },
      {
        key: 'grade',
        label: t('genericTable.classes.grade'),
        options: gradeNames,
        multi: true,
        searchPlaceholder:
          t('schoolDetailsPage.classes.filters.gradeSearchPlaceholder') || 'Search grade',
      },
    ] as FilterField[];
  }, [t, apiData]);

  const gradeIdMapper = useMemo(() => commonUtils.getGradesLabelToIdMapper(t), [t]);

  const toTeacherIds = (value: FilterValue): string[] | undefined => {
    const arr = Array.isArray(value) ? (value as TeacherAutocompleteItem[]) : [];
    return arr.length > 0 ? arr.map(i => i.id) : undefined;
  };

  const toGroupIds = (value: FilterValue): string[] | undefined => {
    const arr = Array.isArray(value) ? (value as GroupAutocompleteItem[]) : [];
    return arr.length > 0 ? arr.map(i => i.groupId) : undefined;
  };

  const toStudentIds = (value: FilterValue): string[] | undefined => {
    const arr = Array.isArray(value) ? (value as StudentAutocompleteItem[]) : [];
    return arr.length > 0 ? arr.map(i => i.id) : undefined;
  };

  const toGradeIds = (value: FilterValue): number[] | undefined => {
    const arr = Array.isArray(value) ? (value as string[]) : [];
    if (arr.length === 0) return undefined;
    const ids = arr.map(label => gradeIdMapper[label]).filter((id): id is number => id != null);
    return ids.length > 0 ? ids : undefined;
  };

  const toClassCodeIds = (value: FilterValue): string[] | undefined => {
    const arr = Array.isArray(value) ? (value as ClassCodeAutocompleteItem[]) : [];
    return arr.length > 0 ? arr : undefined;
  };

  const classCascadingFilters = useMemo(
    (): CascadingFilterRequest => ({
      teacherIds: toTeacherIds(classFilters.teacher),
      groupIds: toGroupIds(classFilters.groupName),
      gradeIds: toGradeIds(classFilters.grade),
      classCodes: toClassCodeIds(classFilters.classCode),
    }),
    [classFilters, gradeIdMapper]
  );

  const studentCascadingFilters = useMemo(
    (): CascadingFilterRequest => ({
      teacherIds: toTeacherIds(studentFilters.teacher),
      groupIds: toGroupIds(studentFilters.groupName),
      studentIds: toStudentIds(studentFilters.name),
      gradeIds: toGradeIds(studentFilters.gradeName),
    }),
    [studentFilters, gradeIdMapper]
  );

  const dashboardCascadingFilters = useMemo(
    (): CascadingFilterRequest => ({
      teacherIds: toTeacherIds(studentDashboardFilters.teacher),
      groupIds: toGroupIds(studentDashboardFilters.group),
      studentIds: toStudentIds(studentDashboardFilters.student),
      gradeIds: toGradeIds(studentDashboardFilters.grade),
    }),
    [studentDashboardFilters, gradeIdMapper]
  );

  useEffect(() => {
    if (classes.length === 0) setSelectedClassId(null);
  }, [classes]);
  useEffect(() => {
    const state = location.state as any;

    if (state?.notification?.message) {
      setNotifMessage(state.notification.message);
      setNotifOpen(true);
      navigate(location.pathname + location.search + location.hash, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, location.search, location.hash, navigate]);

  useEffect(() => {
    if (!addingClass && classes.length > 0 && !selectedClassId) {
      setSelectedClassId(classes[0].id);
    }
  }, [classes, selectedClassId, addingClass]);

  useEffect(() => {
    const currentTab = searchParams.get('tab') || 'general';
    if (currentTab !== 'classes') {
      if (addingClass) setAddingClass(false);
      if (editClass) setEditClass(false);
      if (showStudentInvite) setShowStudentInvite(false);
      return;
    }
  }, [searchParams]);

  // Loading state
  if (isLoading && !isRetrying) {
    return (
      <div className={styles.wrapper}>
        <PageLoader title={String(t('schoolDetailsPage.loadingSchoolDetails'))} />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.errorContainer}>
          <div className={styles.errorIcon}>
            {/* <AppIcon 
              icon={error.type === 'server' ? 'search' : 'warning'} 
              size={64} 
            /> */}
          </div>
          <h2 className={styles.errorTitle}>
            {error.type === 'not_found' ? 'School Not Found' : 'Error Loading School'}
          </h2>
          <p className={styles.errorMessage}>{error.message}</p>

          <div className={styles.errorActions}>
            {canRetry && (
              <Button onClick={retry} loading={isRetrying} className={styles.retryButton}>
                {isRetrying ? 'Retrying...' : 'Try Again'}
              </Button>
            )}
            <Button secondary onClick={refetch}>
              Refresh
            </Button>
            <Button isTextButton onClick={clearError}>
              Dismiss
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const schoolData = apiData?.data?.data?.school;
  if (!schoolData) return <div>{t('schoolDetailsPage.notFound')}</div>;

  const hasActiveTabStatsFilters =
    activeTab === 'students'
      ? hasStudentTabStatsScopeFilters(studentFilterFields, studentFilters)
      : activeTab === 'teachers'
        ? hasStudentTabStatsScopeFilters(teacherFilterFields, teacherFilters)
        : activeTab === 'classes'
          ? hasAnyFilter(classFilterFields, classFilters)
          : false;

  const schoolAdmin = schoolData?.schoolManagerName || '';
  const schoolDistrict = schoolData?.state || '';
  const currentSchoolId = schoolData?.id || '';
  const statistics = apiData?.data?.data?.statistics;

  const statsHiddenForEmptyListTab =
    (activeTab === 'teachers' && !showTeachersStatsSection) ||
    (activeTab === 'classes' && !showClassesStatsSection) ||
    (activeTab === 'students' && studentsTabView === 'data' && !showStudentsStatsSection);

  const showSchoolStatisticsSection =
    !!statistics &&
    !(activeTab === 'students' && studentsTabView === 'metrics') &&
    !statsHiddenForEmptyListTab;

  const ZeroState = ({
    title,
    description,
    buttonText,
    onButtonClick,
    disabledButton = false,
  }: {
    title: string;
    description: string;
    buttonText?: string;
    onButtonClick?: () => void;
    disabledButton?: boolean;
  }) => (
    <div className={stylesZero.zeroStateContainer}>
      <h2 className={stylesZero.zeroStateTitle}>{title}</h2>
      <p className={stylesZero.zeroStateDescription}>{description}</p>
      {buttonText && onButtonClick && (
        <Button
          onClick={onButtonClick}
          className={stylesZero.zeroStateButton}
          disabled={disabledButton}
        >
          {buttonText}
        </Button>
      )}
    </div>
  );

  // Handler for New Class form submit
  const handleClassFormSubmit = (values: any) => {
    setNewClassDraft({
      id: `c_${Date.now()}`,
      name: values.className,
      teacher: teachers.find(t => t.userId === values.teacher)?.userId || '',
      students: 0,
      studentList: [],
    });
    setShowStudentInvite(true);
  };

  return (
    <>
      {/* Header Row - Show appropriate header based on active tab */}
      {activeTab === 'general' && !addingClass && (
        <>
          {userRole === UserRole.Admin ? (
            <PageHeader
              title={currentSchoolName}
              subTitle={user?.metadata?.account?.accountName || ''}
              linkTo={`/edit-school/${currentSchoolId}`}
              buttonText={icons.pencil}
              buttonType='icon'
              onIconClick={() => setIsRenameOpen(true)}
            />
          ) : (
            <PageHeader
              title={currentSchoolName}
              subTitle={schoolAdmin + ' | ' + schoolDistrict}
              linkTo={''}
              buttonText={t('schoolDetailsPage.editSchool') || ''}
            />
          )}
        </>
      )}

      {activeTab === 'teachers' && !addingClass && (
        <PageHeader
          title={t('schoolDetailsPage.teachers.title')}
          subTitle={currentSchoolName || 'School'}
          filterFields={teacherFilterFields}
          filters={teacherFilters}
          disabledFilter={!showTeachersStatistics}
          setFilters={setTeacherFilters}
          organizationId={organizationId}
          linkTo=''
          buttonText={t('schoolDetailsPage.teachers.newTeacher') || ''}
          onButtonClick={() => {
            handleCreateNewTeacher();
          }}
        />
      )}

      {activeTab === 'classes' && !addingClass && (
        <PageHeader
          title={t('schoolDetailsPage.classes.title')}
          subTitle={currentSchoolName || 'School'}
          filterFields={classFilterFields}
          filters={classFilters}
          setFilters={(next: Record<string, FilterValue>) => {
            if (!next?.teacher) {
              setTeacherClassesMode(false);
            }
            setClassFilters(next);
          }}
          organizationId={organizationId}
          linkTo=''
          buttonText={t('schoolDetailsPage.classes.newClass') || ''}
          onButtonClick={handleCreateNewClass}
          disabledButton={teachers.length === 0}
          disabledFilter={!showClassesStatistics || hasSelectedClasses}
          cascadingFilters={classCascadingFilters}
        />
      )}

      {activeTab === 'students' && !addingClass && studentsTabView === 'data' && (
        <PageHeader
          title={t('schoolDetailsPage.students.title')}
          subTitle={currentSchoolName || 'School'}
          filterFields={studentFilterFields}
          filters={studentFilters}
          setFilters={(next: Record<string, FilterValue>) => {
            setStudentFilters({ ...next });
          }}
          organizationId={organizationId}
          disabledFilter={!showStudentsStatistics || hasSelectedStudents}
          cascadingFilters={studentCascadingFilters}
        />
      )}

      {activeTab === 'students' && !addingClass && studentsTabView === 'metrics' && (
        <PageHeader
          title={t('schoolDetailsPage.students.title')}
          subTitle={currentSchoolName || 'School'}
          filterFields={studentDashboardFiltersFields}
          filters={studentDashboardFilters}
          setFilters={setStudentDashboardFilters}
          organizationId={organizationId}
          onBack={studentMetricsDrillDownType ? () => setStudentMetricsDrillDown('') : undefined}
          cascadingFilters={dashboardCascadingFilters}
          disabledFilter={!showStudentsStatistics}
        />
      )}

      {addingClass && (
        <div className={styles.headerRow}>
          <div className={styles.headerRight}>
            <div>{currentSchoolName || 'School'}</div>
          </div>
        </div>
      )}

      {activeTab === 'students' && (
        <PageTabs
          className={styles.studentPageTabs}
          tabs={['data', 'metrics'].map(tabId => ({
            id: tabId,
            label: t(`studentMetricsDashboard.pageTabs.${tabId}`),
          }))}
          selectedTab={studentsTabView}
          onTabSelect={(id: string) => {
            setStudentsTabView(id);
          }}
          viewTabs={!studentMetricsDrillDownType}
          tools={<div ref={studentDashboardToolsRef} />}
        />
      )}

      {showSchoolStatisticsSection && (
        <SchoolStatistics
          statistics={statistics}
          user={user}
          schoolId={currentSchoolId}
          activeTab={activeTab}
          teacherFilters={teacherFilters}
          classFilters={classFilters}
          studentFilters={studentFilters}
          hasActiveTabStatsFilters={hasActiveTabStatsFilters}
          ref={schoolStatisticsRef}
        />
      )}

      {/* Main Content */}
      {!addingClass && (
        <>
          {activeTab === 'general' && (
            <div className={styles.contentRow}>
              <GeneralDetails school={{} as SchoolDetails} apiData={apiData} schoolId={schoolId} />
            </div>
          )}
          {activeTab === 'teachers' && (
            <>
              {/* Render Teachers component for modal, even when no teachers exist */}
              <Teachers
                school={{} as SchoolDetails}
                teachers={teachers}
                setTeachers={setTeachers}
                addingTeacher={addingTeacher}
                setAddingTeacher={setAddingTeacher}
                onCreateNewTeacher={handleCreateNewTeacher}
                editTeacherModalOpen={editTeacherModalOpen}
                setEditTeacherModalOpen={setEditTeacherModalOpen}
                editTeacherData={editTeacherData}
                setEditTeacherData={setEditTeacherData}
                inviteModalOpen={inviteModalOpen}
                setInviteModalOpen={setInviteModalOpen}
                selectedGrade={selectedGrade}
                setSelectedGrade={setSelectedGrade}
                gradeOptions={gradeOptions}
                setTeacherClassesMode={setTeacherClassesMode}
                setClassFilters={setClassFilters}
                teacherFilters={teacherFilters as Record<string, any>}
                hasAnyFilter={hasAnyFilter(teacherFilterFields, teacherFilters)}
                setShowTeachersStatistics={setShowTeachersStatistics}
                setShowTeachersStatsSection={setShowTeachersStatsSection}
                refreshSchoolDetails={refreshSchoolDetails}
              />
            </>
          )}
        </>
      )}

      {activeTab === 'classes' && (
        <Classes
          school={{} as SchoolDetails}
          selectedClassId={selectedClassId}
          setSelectedClassId={setSelectedClassId}
          setShowClassesStatistics={setShowClassesStatistics}
          setShowClassesStatsSection={setShowClassesStatsSection}
          addingClass={addingClass}
          setAddingClass={setAddingClass}
          editClass={editClass}
          setEditClass={setEditClass}
          showStudentInvite={showStudentInvite}
          setShowStudentInvite={setShowStudentInvite}
          teachers={teachers}
          selectedGrade={selectedGrade}
          setSelectedGrade={setSelectedGrade}
          gradeOptions={gradeOptions}
          classFormConfig={createClassFormConfig(teachers, t)}
          handleClassFormSubmit={handleClassFormSubmit}
          newClassDraft={newClassDraft}
          notifType={notifType}
          setNotifType={setNotifType}
          teacherFilterActive={teacherClassesMode}
          classFilters={classFilters}
          onCreateNewClass={handleCreateNewClass}
          hasAnyFilter={hasAnyFilter(classFilterFields, classFilters)}
          refreshSchoolDetails={refreshSchoolDetails}
          onClassesBulkSelectionChange={setHasSelectedClasses}
          onSchoolStatisticsRefetch={() => {
            schoolStatisticsRef.current?.refetch();
          }}
        />
      )}
      {activeTab === 'students' && studentsTabView === 'data' && (
        <Students
          data={baseStudents}
          schoolId={resolvedSchoolId || schoolId}
          studentFilters={studentFilters}
          setStudentFilters={setStudentFilters}
          onHasStudentsChange={setHasStudentsFromComponent}
          hasAnyFilter={hasAnyFilter(studentFilterFields, studentFilters)}
          setShowStudentsStatistics={setShowStudentsStatistics}
          setShowStudentsStatsSection={setShowStudentsStatsSection}
          toolsContainer={studentDashboardToolsRef}
          refreshSchoolDetails={refreshSchoolDetails}
          onStudentsSelected={(selectedStudents: any[]) => {
            setHasSelectedStudents(selectedStudents.length > 0);
          }}
          onRefreshData={() => {
            schoolStatisticsRef.current?.refetch();
          }}
        />
      )}
      {activeTab === 'students' && studentsTabView === 'metrics' && (
        <StudentMetricsDashboard
          filters={studentDashboardApiFilter}
          drillDownType={studentMetricsDrillDownType}
          setDrillDownType={setStudentMetricsDrillDown}
          toolsContainer={studentDashboardToolsRef.current}
        />
      )}
      <ActionNotification
        open={notifOpen}
        message={notifMessage}
        type={notifType}
        position='top-center'
        onClose={() => setNotifOpen(false)}
      />
      <DealLimitReachedMessage
        show={showDealLimitReachedModal}
        onClose={handleCloseDealLimitReachedModal}
      />

      <Popup
        open={isRenameOpen}
        onClose={() => {
          setIsRenameOpen(false);
          setRenameError('');
        }}
        onEnable={async () => {
          if (!newSchoolName || !resolvedSchoolId) {
            setIsRenameOpen(false);
            return;
          }
          const trimmedNameForValidation = newSchoolName.trim();
          if (
            trimmedNameForValidation.length > config.schoolNameMaxLength ||
            !/^[A-Za-z0-9\s'"()–\-:/&*.,֐-׿À-ÿĀ-ǿȀ-ʯ]+$/.test(trimmedNameForValidation)
          ) {
            setRenameError(
              trimmedNameForValidation.length > config.schoolNameMaxLength
                ? (t('dynamicForm.validation.maxLength', {
                    length: config.schoolNameMaxLength,
                  }) as string)
                : (t('dynamicForm.validation.invalidFormat') as string)
            );
            return;
          }
          try {
            const trimmedName = newSchoolName.trim();
            const resp = await adminManagementService.updateOrganizationName?.(
              user?.metadata?.organizationDetails?.organizationId || '',
              trimmedName
            );
            const isSuccess =
              resp?.data?.status === 'success' &&
              resp?.data?.data?.updatedFields?.organizationName === trimmedName;

            if (isSuccess) {
              setCurrentSchoolName(trimmedName);
              setIsRenameOpen(false);
              setNewSchoolName('');
              setRenameError('');
              setNotifMessage(t('dynamicForm.notification.schoolNameUpdated') || '');
              setNotifType('success');
              setNotifOpen(true);
            } else {
              setIsRenameOpen(false);
              setNotifMessage(t('dynamicForm.notification.generalError') + '2');
              setNotifType('error');
              setNotifOpen(true);
            }
          } catch (e) {
            setIsRenameOpen(false);
            setNotifMessage(t('dynamicForm.notification.generalError') + '1');
            setNotifType('error');
            setNotifOpen(true);
          }
        }}
        title={t('dynamicForm.popups.editSchoolName.title') || ''}
        titleStyle={{ marginBottom: 8 }}
        confirmLabel={t('dynamicForm.popups.editSchoolName.ok') || ''}
        showCheckbox={false}
        singleButtonOnly
      >
        <p style={{ marginTop: 0, color: 'rgba(8,38,63,0.7)' }} className={styles.subtitle}>
          {t('dynamicForm.popups.editSchoolName.Subtitles')}
        </p>
        <div style={{ textAlign: 'left' }}>
          <Input
            label={t('dynamicForm.popups.editSchoolName.label') || ''}
            placeholder={t('dynamicForm.popups.editSchoolName.label') || ''}
            value={newSchoolName}
            onChange={e => {
              const value = e.target.value;
              setNewSchoolName(value);

              const trimmedValue = value.trim();
              if (!trimmedValue) {
                setRenameError('');
              } else if (trimmedValue.length > config.schoolNameMaxLength) {
                setRenameError(
                  t('dynamicForm.validation.maxLength', {
                    length: config.schoolNameMaxLength,
                  }) as string
                );
              } else if (!/^[A-Za-z0-9\s'"()–\-:/&*.,֐-׿À-ÿĀ-ǿȀ-ʯ]+$/.test(trimmedValue)) {
                setRenameError(t('dynamicForm.validation.invalidFormat') as string);
              } else {
                setRenameError('');
              }
            }}
            maxLength={config.schoolNameMaxLength}
            error={renameError}
          />
        </div>
      </Popup>
    </>
  );
};

export default SchoolDetailsPage;
